/**
 * Super-admin console navigation (admin-console-split PR 4).
 *
 * The platform operator's console at admin.tktic.com/super-admin. Seven
 * sections in three groups: Platform (clubs + accounts — the tenancy layer),
 * Operations (health, pipeline, storage, live infrastructure), and System
 * (shared infra settings). Visibility is binary — every section is
 * super-admin-only; the shell gates on `is_super_admin` and the server
 * re-gates every route with require_super_admin.
 *
 * Icons are Lucide components (design system), not the legacy numbered
 * glyphs — the sections are not a sequence.
 */

import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Building2,
  Clapperboard,
  HardDrive,
  RadioTower,
  Settings2,
  Users,
} from "lucide-react";

export type SuperAdminSectionId =
  | "clubs"
  | "accounts"
  | "operations"
  | "pipeline"
  | "storage"
  | "live"
  | "system";

export type SuperAdminGroupId = "platform" | "operations" | "system";

export type SuperAdminSection = {
  id: SuperAdminSectionId;
  label: string;
  /** One-line context shown under the label. */
  kicker: string;
  icon: LucideIcon;
  group: SuperAdminGroupId;
  path: string;
};

export const SUPER_ADMIN_SECTIONS: SuperAdminSection[] = [
  // Platform — the tenancy layer: clubs and the global account directory.
  { id: "clubs", label: "Clubs", kicker: "Tenants & onboarding", icon: Building2, group: "platform", path: "/super-admin/clubs" },
  { id: "accounts", label: "Accounts", kicker: "Every sign-in, all clubs", icon: Users, group: "platform", path: "/super-admin/accounts" },
  // Operations — is the platform healthy, and where does it need a hand.
  { id: "operations", label: "Operations", kicker: "Health & job queue", icon: Activity, group: "operations", path: "/super-admin/operations" },
  { id: "pipeline", label: "Video pipeline", kicker: "Encoders & recovery", icon: Clapperboard, group: "operations", path: "/super-admin/pipeline" },
  { id: "storage", label: "Storage", kicker: "Media inventory", icon: HardDrive, group: "operations", path: "/super-admin/storage" },
  { id: "live", label: "Live infrastructure", kicker: "Ingest & viewers", icon: RadioTower, group: "operations", path: "/super-admin/live" },
  // System — settings shared by every club.
  { id: "system", label: "System", kicker: "Email & sign-in providers", icon: Settings2, group: "system", path: "/super-admin/system" },
];

export const SUPER_ADMIN_GROUPS: Array<{ id: SuperAdminGroupId; label: string }> = [
  { id: "platform", label: "Platform" },
  { id: "operations", label: "Operations" },
  { id: "system", label: "System" },
];

export function superAdminSectionForPath(pathname: string): SuperAdminSectionId {
  const hit = SUPER_ADMIN_SECTIONS.find(
    (s) => pathname === s.path || pathname.startsWith(s.path + "/"),
  );
  return hit?.id ?? "clubs";
}
